/**
 * The identity-level reclaim list — admin only.
 *
 * This is the one screen in the licence board that shows employee personal
 * data, so three things are true of it at once:
 *
 * 1. It is mounted inside `<AdminRoute>`, so a non-admin never reaches it.
 * 2. The backend independently checks the session role and returns an empty
 *    `accounts` array to anyone else — the route guard is a convenience, not
 *    the control.
 * 3. Nothing here is persisted. The rows are derived per request and held in
 *    the backend's memory for two minutes at most.
 *
 * If the backend answers with `admin: false` — a stale cookie, a role change
 * mid-session — this renders the aggregate counts and says why, rather than an
 * empty table that reads like "nothing to reclaim".
 */

import { Icon, ShieldAlert, Users } from "@norican/icons";
import {
  Alert,
  Badge,
  Card,
  DataGrid,
  EmptyState,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";

import { type ReclaimableAccount, useReclaimableAccounts } from "@/lib/api";

import { formatSeats, RECLAIM_REASON_LABEL } from "./licence-format";

/** Tone per reason, so the badge colour carries the same meaning everywhere. */
const REASON_TONE: Record<string, "danger" | "warning" | "caution"> = {
  disabled: "danger",
  guest: "caution",
  "inactive-90d": "warning",
};

export function ReclaimableAccountsPage() {
  const { data, isPending, error } = useReclaimableAccounts();

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Reclaimable accounts"
        subtitle="Licensed accounts that are disabled, external, or long idle."
      />

      <Alert tone="info" title="Personal data" icon={<Icon icon={ShieldAlert} size="sm" />}>
        This list identifies individual employees and is restricted to administrators. It is derived
        on request, never stored, and must not be exported into anything that keeps it.
      </Alert>

      {error && (
        <Alert tone="danger" title="Couldn't load the account list">
          {error instanceof Error ? error.message : "The licence API did not respond."}
        </Alert>
      )}

      {isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data && (
          <>
            {!data.admin && (
              <Alert tone="warning" title="Showing counts only">
                {data.restricted_detail ??
                  "This session does not hold the admin role, so individual accounts are withheld."}
              </Alert>
            )}

            <Card variant="outline">
              <Card.Header>
                <div className="flex flex-wrap items-baseline justify-between gap-4">
                  <Typography variant="heading-4" as="h2" className="flex items-center gap-2">
                    <Icon icon={Users} size="sm" />
                    Candidates
                  </Typography>
                  <Typography variant="body-sm" tone="muted">
                    {formatSeats(data.summary.disabled_licensed)} disabled ·{" "}
                    {formatSeats(data.summary.guest_licensed)} guests ·{" "}
                    {data.summary.sign_in_data_available
                      ? `${formatSeats(data.summary.inactive_90d)} idle 90d+`
                      : "idle unavailable"}
                  </Typography>
                </div>
              </Card.Header>
              <Card.Body padding={data.accounts.length === 0 ? "md" : "none"}>
                {data.accounts.length === 0 ? (
                  <EmptyState
                    size="sm"
                    title={data.admin ? "No reclaim candidates" : "Accounts withheld"}
                    description={
                      data.admin
                        ? "Every licensed account is enabled, internal, and recently active."
                        : "Sign in with an administrator role to see which accounts the counts refer to."
                    }
                  />
                ) : (
                  <div className="h-[28rem]">
                    {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
                        sticky header only get a scroll region inside a DEFINITE height.
                        Without one they latch onto the page viewport and drift while the
                        page scrolls. Every DataGrid story wraps the grid this way. */}
                    <DataGrid<ReclaimableAccount>
                      data={data.accounts}
                      getRowId={(account) => account.id}
                      getRowSearchText={(account) =>
                        `${account.display_name} ${account.user_principal_name}`
                      }
                      density="compact"
                      aria-label="Licensed accounts that may be reclaimable"
                      columns={[
                        {
                          id: "name",
                          header: "Account",
                          accessor: (account) => account.display_name,
                          sortable: true,
                          pinned: "left",
                          cell: (account) => (
                            <div className="flex flex-col">
                              <span className="text-fg font-medium">{account.display_name}</span>
                              <span className="text-fg-muted text-xs">
                                {account.user_principal_name}
                              </span>
                            </div>
                          ),
                        },
                        {
                          id: "reasons",
                          header: "Why",
                          cell: (account) => (
                            <div className="flex flex-wrap gap-1">
                              {account.reasons.map((reason) => (
                                <Badge
                                  key={reason}
                                  tone={REASON_TONE[reason] ?? "neutral"}
                                  variant="subtle"
                                  size="xs"
                                >
                                  {RECLAIM_REASON_LABEL[reason] ?? reason}
                                </Badge>
                              ))}
                            </div>
                          ),
                        },
                        {
                          id: "type",
                          header: "Type",
                          accessor: (account) => account.user_type,
                          sortable: true,
                          cell: (account) => (
                            <span className="text-fg-muted text-sm">{account.user_type}</span>
                          ),
                        },
                        {
                          id: "licences",
                          header: "Licences",
                          numeric: true,
                          sortable: true,
                          accessor: (account) => account.licence_count,
                          cell: (account) => (
                            <span className="text-fg tabular-nums">{account.licence_count}</span>
                          ),
                        },
                        {
                          id: "idle",
                          header: "Days idle",
                          numeric: true,
                          sortable: true,
                          accessor: (account) => account.days_since_sign_in ?? null,
                          cell: (account) =>
                            account.days_since_sign_in == null ? (
                              <span className="text-fg-subtle text-xs">Never signed in</span>
                            ) : (
                              <span className="text-fg tabular-nums">
                                {formatSeats(account.days_since_sign_in)}
                              </span>
                            ),
                        },
                      ]}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )
      )}
    </main>
  );
}
